// Dashboard Sport — séances, calories brûlées, stats et courbe.
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { type FormEvent, useMemo, useState } from 'react';
import { ExerciseChart } from '@/components/charts';
import { ChartCard, EmptyState, ErrorBanner, Kpi, PeriodPicker } from '@/components/ui';
import {
  type Activity,
  dailyBurned,
  dayKey,
  exerciseStats,
  filterByPeriod,
  nowLocalInputValue,
  type Period,
  SPORT_LABELS,
  SPORT_ORDER,
  todayKey,
} from '@/domain';
import { useCreateExercise, useDeleteExercise, useExercises } from '@/hooks/queries';
import { useSubmit } from '@/hooks/useSubmit';
import { confirmDelete } from '@/lib/confirm';
import { useTheme } from '@/theme/ThemeContext';

export function ExercisePage() {
  const { data: exercises = [] } = useExercises();
  const createExercise = useCreateExercise();
  const deleteExercise = useDeleteExercise();
  const submit = useSubmit();
  const { palette } = useTheme();

  const [activity, setActivity] = useState<Activity>('running');
  const [kcal, setKcal] = useState('');
  const [duration, setDuration] = useState('');
  const [note, setNote] = useState('');
  const [when, setWhen] = useState(nowLocalInputValue());
  const [period, setPeriod] = useState<Period>('30d');

  const stats = useMemo(() => exerciseStats(exercises), [exercises]);
  const chart = useMemo(
    () => dailyBurned(filterByPeriod(exercises, (e) => e.datetime, period)),
    [exercises, period],
  );
  const todayEntries = exercises.filter((e) => dayKey(e.datetime) === todayKey());

  function addExercise(e: FormEvent) {
    e.preventDefault();
    const burned = Number(kcal);
    if (!kcal || !Number.isFinite(burned) || burned < 0) return;
    const mins = Number(duration) || 0;
    void submit.run(async () => {
      await createExercise.mutateAsync({
        activity,
        kcal_burned: burned,
        duration_min: mins,
        note: note.trim(),
        datetime: new Date(when).toISOString(),
      });
      setKcal('');
      setDuration('');
      setNote('');
      setWhen(nowLocalInputValue());
    });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Sport</h1>
          <p>Séances et calories dépensées (en plus de la maintenance).</p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      <form className="card toolbar" onSubmit={addExercise} style={{ marginBottom: '1rem' }}>
        <div>
          <label htmlFor="activity">Activité</label>
          <select
            id="activity"
            value={activity}
            onChange={(e) => setActivity(e.target.value as Activity)}
          >
            {SPORT_ORDER.map((a) => (
              <option key={a} value={a}>
                {SPORT_LABELS[a]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="kcal">Calories (kcal)</label>
          <input
            id="kcal"
            type="number"
            min={0}
            max={10000}
            value={kcal}
            onChange={(e) => setKcal(e.target.value)}
            placeholder="ex. 350"
            style={{ maxWidth: 110 }}
            required
          />
        </div>
        <div>
          <label htmlFor="duration">Durée (min)</label>
          <input
            id="duration"
            type="number"
            min={0}
            max={1440}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="30"
            style={{ maxWidth: 90 }}
          />
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label htmlFor="note">Note</label>
          <input
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="ex. 5 km"
            maxLength={300}
          />
        </div>
        <div>
          <label htmlFor="when">Quand</label>
          <input
            id="when"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
        </div>
        <button type="submit" disabled={submit.pending}>
          Ajouter
        </button>
      </form>
      {submit.error && <ErrorBanner message={submit.error} onRetry={submit.reset} />}

      <div className="grid kpis" style={{ marginBottom: '1rem' }}>
        <Kpi label="Brûlé aujourd'hui" value={stats.today} unit="kcal" />
        <Kpi label="Moyenne / jour" value={stats.avgKcalPerDay} unit="kcal" />
        <Kpi label="Séances" value={stats.sessions} hint={`${stats.daysLogged} jours actifs`} />
        <Kpi label="Série en cours" value={stats.currentStreak} unit="j" />
      </div>

      {exercises.length === 0 ? (
        <EmptyState>Ajoute ta première séance pour démarrer le suivi.</EmptyState>
      ) : (
        <>
          <ChartCard
            title="Calories brûlées par jour"
            subtitle="dépense d'exercice (hors maintenance)"
          >
            <ExerciseChart data={chart} palette={palette} />
          </ChartCard>

          <div className="card" style={{ marginTop: '1rem' }}>
            <h3>Séances du jour</h3>
            {todayEntries.length === 0 ? (
              <p className="muted" style={{ fontSize: '0.85rem' }}>
                Rien de saisi aujourd'hui.
              </p>
            ) : (
              <div className="table-wrap" style={{ marginTop: '0.5rem' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Heure</th>
                      <th>Activité</th>
                      <th>kcal</th>
                      <th>Durée</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayEntries.map((ex) => (
                      <tr key={ex.id}>
                        <td>{format(parseISO(ex.datetime), 'HH:mm', { locale: fr })}</td>
                        <td>{ex.activity ? SPORT_LABELS[ex.activity] : '—'}</td>
                        <td>{ex.kcalBurned} kcal</td>
                        <td>{ex.durationMin ? `${ex.durationMin} min` : '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="danger"
                            onClick={() => {
                              if (confirmDelete('cette séance')) deleteExercise.mutate(ex.id);
                            }}
                          >
                            Suppr.
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
